// A GiraffeData implementation backed by literal rows. Used by the tests and by
// `?demo`, so the whole app runs with no Giraffe attached.

import type { DataRow, GiraffeData, MeasureClass, SourceKind } from './types';
import { classifyAll } from './classify';
import { areaDataRows } from './areasTable';

/** The sources this fixture stores directly — `'areas'` is computed from the other three, not stored. */
type StoredSource = Exclude<SourceKind, 'areas'>;

export interface FixtureInput {
  bakedSections?: DataRow[];
  rawSections?: DataRow[];
  blocks?: Record<string, { id: string; name?: string; properties?: Record<string, unknown> }>;
  usages?: DataRow[];
  /** Drawing layers the project offers, including ones nothing is drawn on. */
  layers?: string[];
  /** Subset of `layers` to report as visible — defaults to all of `layers`, since a fixture has no layer panel to actually hide one. */
  visibleLayers?: string[];
}

export class FixtureData implements GiraffeData {
  readonly live = false;
  private readonly blockDefs: Record<string, { id: string; name?: string; properties?: Record<string, unknown> }>;

  blocks() {
    return this.blockDefs;
  }

  version = 1;
  private data: Record<StoredSource, DataRow[]>;
  private fieldCache = new Map<SourceKind, string[]>();
  private classCache = new Map<SourceKind, Map<string, MeasureClass>>();
  private selection: Set<string> | null = null;
  /** A plausible site for the demo scheme, so plot ratio reads sensibly. */
  private site: number | null = 12_400;

  private readonly layers: string[];
  private readonly visibleLayers: string[];

  constructor(input: FixtureInput = {}) {
    this.blockDefs = input.blocks ?? {};
    this.layers = input.layers ?? [];
    this.visibleLayers = input.visibleLayers ?? this.layers;
    this.data = {
      bakedSections: input.bakedSections ?? [],
      rawSections: input.rawSections ?? [],
      usages: input.usages ?? [],
    };
  }

  replace(input: FixtureInput): void {
    this.data = {
      bakedSections: input.bakedSections ?? [],
      rawSections: input.rawSections ?? [],
      usages: input.usages ?? [],
    };
    this.fieldCache.clear();
    this.classCache.clear();
    this.version += 1;
  }

  setSelection(keys: string[] | null): void {
    this.selection = keys ? new Set(keys) : null;
    this.version += 1;
  }

  /** `'areas'` is computed from the Areas sheet's own merge logic, not stored — see `data/areasTable.ts`'s `areaDataRows`. */
  private rowsFor(source: SourceKind): readonly DataRow[] {
    return source === 'areas' ? areaDataRows(this) : this.data[source];
  }

  rows(source: SourceKind): readonly DataRow[] {
    return this.rowsFor(source);
  }

  fields(source: SourceKind): readonly string[] {
    let f = this.fieldCache.get(source);
    if (!f) {
      const set = new Set<string>();
      for (const r of this.rowsFor(source)) for (const k of Object.keys(r.properties)) set.add(k);
      f = [...set].sort();
      this.fieldCache.set(source, f);
    }
    return f;
  }

  classOf(source: SourceKind, field: string): MeasureClass {
    let m = this.classCache.get(source);
    if (!m) {
      m = classifyAll(this.fields(source), this.rowsFor(source));
      this.classCache.set(source, m);
    }
    return m.get(field) ?? 'rate';
  }

  drawingLayers(): readonly string[] {
    return this.layers;
  }

  visibleDrawingLayers(): readonly string[] {
    return this.visibleLayers;
  }

  selectionKeys(): ReadonlySet<string> | null {
    return this.selection;
  }

  siteArea(): number | null {
    return this.site;
  }

  setSiteArea(value: number | null): void {
    this.site = value;
    this.version += 1;
  }

  inferredClassOf(source: SourceKind, field: string): MeasureClass {
    return this.classOf(source, field);
  }
}

/**
 * Wraps a `GiraffeData` so `'areas'` resolves to `alias` instead of the real
 * Areas computation (`mergeAreaRows`'s block/level split and relabeling) —
 * for a test exercising generic engine mechanics (GI.* aggregation, sheet
 * filters, stamped schedules) against a plain `bakedSections` fixture that
 * predates Areas and is not testing it. Delegates every method by closure,
 * never by spreading the instance, since `FixtureData`'s methods live on its
 * prototype and a spread would silently drop them.
 */
export function withAreasAliasedTo(data: GiraffeData, alias: SourceKind): GiraffeData {
  const remap = (source: SourceKind): SourceKind => (source === 'areas' ? alias : source);
  return {
    get version() {
      return data.version;
    },
    get live() {
      return data.live;
    },
    rows: (source) => data.rows(remap(source)),
    fields: (source) => data.fields(remap(source)),
    classOf: (source, field) => data.classOf(remap(source), field),
    selectionKeys: () => data.selectionKeys(),
    blocks: () => data.blocks(),
    siteArea: () => data.siteArea(),
    inferredClassOf: (source, field) => (data.inferredClassOf ?? data.classOf).call(data, remap(source), field),
    geometryFor: data.geometryFor ? (id: string) => data.geometryFor!(id) : undefined,
    drawingLayers: data.drawingLayers ? () => data.drawingLayers!() : undefined,
    visibleDrawingLayers: data.visibleDrawingLayers ? () => data.visibleDrawingLayers!() : undefined,
    units: data.units ? () => data.units!() : undefined,
  };
}

/** A small, realistic mixed-use scheme. Deliberately includes a nine-level stack. */
export function demoSections(): DataRow[] {
  const mk = (
    id: string,
    usage: string,
    area: number,
    levels: number,
    salePrice: number,
    hardCost: number,
    units: number,
    name: string
  ): DataRow => ({
    key: `${id}#${area}:${levels}`,
    id,
    properties: {
      id,
      name,
      usage,
      area,
      perimeter: Math.round(4 * Math.sqrt(area)),
      levels,
      grossArea: area * levels,
      netArea: Math.round(area * levels * 0.85),
      saleableArea: Math.round(area * levels * 0.78),
      salePrice,
      hardCost,
      efficiency: 0.85,
      floorToFloor: 3.2,
      dwellingTotalCount: units,
      'dwelling-1BED-Count': Math.round(units * 0.35),
      'dwelling-2BED-Count': Math.round(units * 0.5),
      'dwelling-3BED-Count': units - Math.round(units * 0.35) - Math.round(units * 0.5),
      _height: levels * 3.2,
    },
  });

  return [
    mk('twr-a', 'Residential', 1097, 9, 8500, 3200, 112, 'Tower A'),
    mk('twr-b', 'Residential', 940, 12, 8900, 3300, 128, 'Tower B'),
    mk('pod-1', 'Retail', 1400, 1, 6000, 2600, 0, 'Podium Retail'),
    mk('pod-2', 'Commercial', 1250, 4, 7200, 2900, 0, 'Office Block'),
    mk('car-1', 'Parking', 1800, 2, 0, 1400, 0, 'Basement Parking'),
  ];
}

export function demoUsages(): DataRow[] {
  // Shaped like a real Giraffe usage: rates, geometry defaults, appearance and
  // a nested dwelling mix. The app must show all of it — a whitelist of the
  // fields it expects loses whatever a project actually carries.
  const mk = (
    name: string,
    salePrice: number,
    hardCost: number,
    efficiency: number,
    color: string,
    floorToFloor: number,
    dwellingMix?: Record<string, number>
  ): DataRow => ({
    key: `usage:${name}`,
    id: name,
    properties: {
      name,
      usage: name,
      salePrice,
      hardCost,
      softCost: Math.round(hardCost * 0.18),
      efficiency,
      floorToFloor,
      height: floorToFloor,
      color,
      lineColor: '#704A07',
      fillOpacity: 1,
      ...(dwellingMix ? { dwellingMix } : {}),
    },
  });
  return [
    mk('Residential', 8700, 3250, 0.85, '#FBAD27', 3.2, { '1BED': 0.35, '2BED': 0.5, '3BED': 0.15 }),
    mk('Retail', 6000, 2600, 0.9, '#E36414', 4.5),
    mk('Commercial', 7200, 2900, 0.82, '#3A86FF', 3.8),
    mk('Parking', 0, 1400, 0.95, '#8D99AE', 2.8),
  ];
}

/**
 * The features as the user DREW them, before the engine baked them.
 *
 * Fewer fields and no computed areas: a raw section carries what someone typed
 * plus what the app put there, which is exactly what the Features sheet is for
 * editing. Two of them place the same block, so the Blocks sheet has an
 * instance count worth reading.
 */
export function demoRawSections(): DataRow[] {
  const mk = (id: string, name: string, usage: string, extra: Record<string, unknown> = {}): DataRow => ({
    key: `raw:${id}`,
    id,
    properties: { id, name, usage, layerId: 'design', ...extra },
  });
  return [
    mk('f-a1', 'Tower A', 'Residential', { levels: 9, blockId: 'blk-plate', groupName: 'North block' }),
    mk('f-a2', 'Tower B', 'Residential', { levels: 6, blockId: 'blk-plate', groupName: 'North block' }),
    mk('f-r1', 'Retail podium', 'Retail', { levels: 1, blockId: 'blk-podium' }),
    mk('f-c1', 'Office slab', 'Commercial', { levels: 4 }),
    mk('f-p1', '', 'Parking', { levels: 2 }),
  ];
}

/** Two definitions, one of them placed twice, so instance counts read. */
export function demoBlocks(): Record<string, { id: string; name?: string; properties?: Record<string, unknown> }> {
  return {
    'blk-plate': { id: 'blk-plate', name: 'Resi floor plate', properties: { floorPlate: true } },
    'blk-podium': { id: 'blk-podium', name: 'Retail podium', properties: {} },
  };
}

export function demoData(): FixtureData {
  return new FixtureData({
    bakedSections: demoSections(),
    rawSections: demoRawSections(),
    usages: demoUsages(),
    blocks: demoBlocks(),
  });
}
